import axios from 'axios';
import { Resource } from '@domain/base/Resource';
import { ChannelDockAccountType, IChannelDockConnectorRepository } from '@domain/connection/IMarketplaceRepository';
import { Connection } from '@domain/connection/models/Connection';
import { InventorySyncEventItem } from '@domain/connection/models/InventorySyncEvent';
import { FullSyncJob } from '@domain/connection/models/ImportJob';
import { ChannelDockConnectorDataSource } from '../datasources/ChannelDockConnectorDataSource';
import { ConnectionMapper } from '../mappers/MarketplaceMapper';

function errorMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { message?: string | string[] } | undefined;
    const message = Array.isArray(data?.message) ? data.message.join(', ') : data?.message;
    if (message) return message;
  }
  return fallback;
}

export class ChannelDockConnectorRepository implements IChannelDockConnectorRepository {
  constructor(private dataSource: ChannelDockConnectorDataSource) { }

  async addConnection(apiKey: string, apiSecret: string, accountType: ChannelDockAccountType): Promise<Resource<Connection>> {
    try {
      const data = await this.dataSource.addConnection(apiKey, apiSecret, accountType);
      return { status: 'success', data: ConnectionMapper.toConnectionModel(data) };
    } catch (error) {
      return { status: 'error', error: { type: 'UnknownError', message: errorMessage(error, 'Failed to add ChannelDock connection') } };
    }
  }

  async reconnect(connectionId: string, apiKey: string, apiSecret: string, accountType: ChannelDockAccountType): Promise<Resource<Connection>> {
    try {
      const data = await this.dataSource.reconnect(connectionId, apiKey, apiSecret, accountType);
      return { status: 'success', data: ConnectionMapper.toConnectionModel(data) };
    } catch (error) {
      return { status: 'error', error: { type: 'UnknownError', message: errorMessage(error, 'Failed to reconnect ChannelDock') } };
    }
  }

  async fetchInventory(connectionId: string): Promise<Resource<InventorySyncEventItem[]>> {
    try {
      const data = await this.dataSource.fetchInventory(connectionId);
      return { status: 'success', data };
    } catch (error) {
      return { status: 'error', error: { type: 'UnknownError', message: errorMessage(error, 'Failed to fetch ChannelDock inventory') } };
    }
  }

  async syncFull(connectionId: string): Promise<Resource<FullSyncJob[]>> {
    try {
      const data = await this.dataSource.syncFull(connectionId);
      return { status: 'success', data };
    } catch (error) {
      return { status: 'error', error: { type: 'UnknownError', message: errorMessage(error, 'Failed to start ChannelDock sync') } };
    }
  }
}
